import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { logEvent } from './services/firebase';
import { PrivateRoute } from './components/layout/PrivateRoute';
import { Navbar } from './components/layout/Navbar';

import Login from './components/pages/Login';
import Dashboard from './components/pages/Dashboard';
import Discover from './components/pages/Discover';
import WheelList from './components/pages/WheelList';
import WheelDetail from './components/pages/WheelDetail';
import EarningsList from './components/pages/EarningsList';
import EarningsDetail from './components/pages/EarningsDetail';
import Settings from './components/pages/Settings';
import AddTicker from './components/pages/AddTicker';
import TraderPreferences from './components/pages/TraderPreferences';
import TrackRecord from './components/pages/TrackRecord';
import MyTrades from './components/pages/MyTrades';
import Alerts from './components/pages/Alerts';
import TickerAdmin from './components/pages/admin/TickerAdmin';
import JobTriggers from './components/pages/admin/JobTriggers';
import SystemInfo from './components/pages/admin/SystemInfo';
import MaxPainExplorer from './components/pages/admin/MaxPainExplorer';
import PaperTradeAdmin from './components/pages/admin/PaperTradeAdmin';
import GEXAnalysis from './components/pages/GEXAnalysis';
import MarketPulse from './components/pages/MarketPulse';
import TermsAdmin from './components/pages/admin/TermsAdmin';
import NewsletterAdmin from './components/pages/admin/NewsletterAdmin';
import PulseScorecard from './components/pages/admin/PulseScorecard';
import PulseEval from './components/pages/PulseEval';
import Landing from './components/pages/Landing';
import TermsPage from './components/pages/TermsPage';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
    <Navbar />
    <Box component="main" sx={{ flexGrow: 1 }}>
      {children}
    </Box>
  </Box>
);

const RouteTracker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  useEffect(() => {
    logEvent('page_view', { page_path: location.pathname + location.search });
  }, [location]);
  return <>{children}</>;
};

const App: React.FC = () => (
  <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <RouteTracker>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
          <Route path="/signal-performance" element={<Navigate to="/signal-report/earnings" replace />} />

          <Route
            path="/*"
            element={
              <PrivateRoute>
                <Layout>
                  <Routes>
                    <Route path="/" element={<Navigate to="/discover" replace />} />
                    <Route path="/discover" element={<Discover />} />
                    <Route path="/discover/wheel/:ticker" element={<WheelDetail />} />
                    <Route path="/discover/earnings/:ticker" element={<EarningsDetail />} />

                    {/* Portfolio */}
                    <Route path="/portfolio/positions" element={<MyTrades />} />
                    <Route path="/portfolio/wheel" element={<WheelList />} />
                    <Route path="/portfolio/wheel/:id" element={<WheelDetail />} />
                    <Route path="/portfolio/earnings" element={<EarningsList />} />
                    <Route path="/portfolio/earnings/:id" element={<EarningsDetail />} />
                    <Route path="/portfolio/gamma-exposure" element={<GEXAnalysis />} />
                    <Route path="/portfolio/gamma-exposure/:ticker" element={<GEXAnalysis />} />

                    {/* Signal Report */}
                    <Route path="/signal-report/pulse" element={<PulseEval />} />
                    <Route path="/signal-report/earnings" element={<TrackRecord />} />

                    {/* Pulse */}
                    <Route path="/pulse" element={<MarketPulse />} />
                    <Route path="/pulse/:ticker" element={<MarketPulse />} />

                    {/* Settings & Admin */}
                    <Route path="/alerts" element={<Alerts />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/settings/preferences" element={<TraderPreferences />} />
                    <Route path="/settings/add-ticker" element={<AddTicker />} />
                    <Route path="/admin/tickers" element={<TickerAdmin />} />
                    <Route path="/admin/jobs" element={<JobTriggers />} />
                    <Route path="/admin/system" element={<SystemInfo />} />
                    <Route path="/admin/max-pain" element={<MaxPainExplorer />} />
                    <Route path="/admin/paper-trades" element={<PaperTradeAdmin />} />
                    <Route path="/admin/newsletter" element={<NewsletterAdmin />} />
                    <Route path="/admin/pulse-scorecard" element={<PulseScorecard />} />
                    <Route path="/admin/terms" element={<TermsAdmin />} />
                    <Route path="/dashboard" element={<Dashboard />} />

                    {/* Redirects for old paths */}
                    <Route path="/trades" element={<Navigate to="/portfolio/positions" replace />} />
                    <Route path="/wheel" element={<Navigate to="/portfolio/wheel" replace />} />
                    <Route path="/wheel/:id" element={<Navigate to="/portfolio/wheel" replace />} />
                    <Route path="/earnings" element={<Navigate to="/portfolio/earnings" replace />} />
                    <Route path="/gex" element={<Navigate to="/portfolio/gamma-exposure" replace />} />
                    <Route path="/gex/:ticker" element={<Navigate to="/portfolio/gamma-exposure" replace />} />
                    <Route path="/portfolio/gex" element={<Navigate to="/portfolio/gamma-exposure" replace />} />
                    <Route path="/portfolio/gex/:ticker" element={<Navigate to="/portfolio/gamma-exposure" replace />} />
                    <Route path="/pulse/eval" element={<Navigate to="/signal-report/pulse" replace />} />
                  </Routes>
                </Layout>
              </PrivateRoute>
            }
          />
        </Routes>
        </RouteTracker>
      </BrowserRouter>
    </AuthProvider>
  </ThemeProvider>
);

export default App;
